/**
 * Tiny wrappers around the `<link rel="…">` resource-hint API.
 *
 * Used today by DocumentShow to:
 *   - `preconnect` to the file storage origin so the viewer's PDF
 *     fetch doesn't pay TCP+TLS handshake cost on a cold connection.
 *   - `preload` the PDF bytes the moment the URL is known, in parallel
 *     with React mount + viewer initialization.
 *
 * Browsers that don't support a given hint quietly ignore the link tag,
 * so these are safe to fire unconditionally.
 */

export type ResourceHintRel =
  | 'preconnect'
  | 'preload'
  | 'prefetch'
  | 'dns-prefetch';

export type ResourceHintAttrs = {
  /** Required for `preload`. Tells the browser what kind of resource to expect (e.g. `'fetch'`, `'image'`). */
  as?: string;
  /** Use `'anonymous'` for cross-origin GETs that don't carry credentials. */
  crossOrigin?: 'anonymous' | 'use-credentials';
};

/**
 * Inject a `<link rel="…">` element into `document.head`. Returns the
 * created element so the caller can clean it up later (or null when no
 * `href` was provided).
 */
export function injectResourceHint(
  rel: ResourceHintRel,
  href: string | undefined | null,
  attrs: ResourceHintAttrs = {},
): HTMLLinkElement | null {
  if (!href) return null;
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  // Use setAttribute for `as` and `crossorigin` — the typed property setters
  // are inconsistent across DOM implementations (jsdom in particular ignores
  // the `as` property setter), and setAttribute lets the browser pick the
  // right idiom from the markup.
  if (attrs.as) link.setAttribute('as', attrs.as);
  if (attrs.crossOrigin) link.setAttribute('crossorigin', attrs.crossOrigin);
  document.head.appendChild(link);
  return link;
}

/** Remove a previously-injected link element. Safe to call with null. */
export function removeResourceHint(link: HTMLLinkElement | null): void {
  if (!link) return;
  if (link.parentNode) link.parentNode.removeChild(link);
}
